/**
 * 节点级附件上传组件（对齐后台 UpdateNodeDialog 的节点单证区域）。
 *
 * 与子步骤面板内的附件上传区别：
 * - 本组件处理「节点级」附件规则（node_doc_rules 中 substepCode 为空/未指定的规则）
 * - 上传时 bizType='node_doc', bizId=orderId, bizId2=nodeCode, bizId3=docType
 * - 文件变化后通过 onChange 回传 {docType, fileId}[] 给父组件，用于 advance.files
 *
 * 多份支持（2026-10-19）：rule.allowMultiple=true 时，同一 docType 可保存多个文件
 * （数组结构，与后台 ContainerArchive / 子步骤面板口径一致）；false 时仅保留最新一份
 * （重新上传语义）。onChange 会回传每个 docType 下的全部文件，后端按 bizId3 落 file_attachments。
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { listFilesByBiz, uploadFileToServer, getFileUrl, getFileInlineUrl, deleteFile } from '@/api/storage';
import type { NodeDocRuleItem } from '@/api/substep';
import type { DocFilePair } from '@/api/types';
import { useDict } from '@/hooks/useDict';
import { isWeChatBrowser } from '@/utils/device';
import ImagePreview from './ImagePreview';

interface UploadedFile {
  name: string;
  fileId?: string;
  previewUrl?: string;
  isImage: boolean;
  isPdf?: boolean;
}

/** 按文件名判断文件展示类型 */
function detectFileType(name: string): { isImage: boolean; isPdf: boolean } {
  const lower = name.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
  const isPdf = lower.endsWith('.pdf');
  return { isImage, isPdf };
}

/** 根据文件类型选择预览 URL 接口：PDF 走 inline-url 避免触发下载 */
function choosePreviewUrl(fileId: string, isPdf: boolean): Promise<string> {
  return isPdf ? getFileInlineUrl(fileId) : getFileUrl(fileId);
}

export interface NodeDocUploaderProps {
  orderId: string;
  nodeCode: string;
  rules: NodeDocRuleItem[];
  onChange?: (files: DocFilePair[]) => void;
}

export default function NodeDocUploader({ orderId, nodeCode, rules, onChange }: NodeDocUploaderProps) {
  // docType 中文名走字典（doc_type），未命中回退原始 code；与 nodeConfig 策略一致
  const { getLabel } = useDict();
  // 微信内置浏览器只支持 image/*（拍摄/相册），PDF 需引导用系统浏览器打开
  const weChat = isWeChatBrowser();
  // 每种附件类型保留已上传文件列表：单份规则数组长度最多为 1，多份规则（allowMultiple）可持续追加
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFile[]>>({});
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type?: 'image' | 'pdf' } | null>(null);

  /** 把当前 state 摊平成 DocFilePair[] 回传父组件（每个 docType 下的全部文件） */
  const notifyChange = useCallback(
    (files: Record<string, UploadedFile[]>) => {
      const pairs: DocFilePair[] = Object.entries(files)
        .flatMap(([docType, arr]) => (arr || []).map((f) => ({ docType, fileId: f.fileId })))
        .filter((p): p is DocFilePair => !!p.fileId);
      onChange?.(pairs);
    },
    [onChange],
  );

  // state 变化即通知父组件（用 effect 而非在每次 set 后手动调，避免批量多选时的闭包陈旧问题）
  useEffect(() => {
    notifyChange(uploadedFiles);
  }, [uploadedFiles, notifyChange]);

  useEffect(() => {
    if (rules.length === 0) return;
    let cancelled = false;
    setLoading(true);
    listFilesByBiz('node_doc', orderId, nodeCode)
      .then(async (existingFiles) => {
        const filesArray = Array.isArray(existingFiles) ? existingFiles : [];
        const filesByDocType: Record<string, UploadedFile[]> = {};
        for (const rule of rules) {
          // 该 docType 的所有已传文件（按上传时间倒序），允许多份则全留，否则只回显最新一份
          const matched = filesArray
            .filter((f) => f.bizId3 === rule.docType)
            .sort((a, b) =>
              String((b as { createdAt?: string }).createdAt ?? '').localeCompare(
                String((a as { createdAt?: string }).createdAt ?? ''),
              ),
            );
          const selected = rule.allowMultiple ? matched : matched.slice(0, 1);
          if (selected.length === 0) continue;
          const built: UploadedFile[] = [];
          for (const mf of selected) {
            const name = mf.fileName ?? '文件';
            const { isImage, isPdf } = detectFileType(name);
            let previewUrl: string | undefined;
            try {
              previewUrl = await choosePreviewUrl(mf.id, isPdf);
            } catch {
              previewUrl = undefined;
            }
            built.push({ name, fileId: mf.id, previewUrl, isImage, isPdf });
          }
          filesByDocType[rule.docType] = built;
        }
        if (!cancelled) {
          setUploadedFiles(filesByDocType);
        }
      })
      .catch((err) => {
        console.error('加载节点已上传文件失败', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, nodeCode, rules]);

  const handleUpload = async (docType: string, file: File) => {
    const allowMultiple = rules.find((r) => r.docType === docType)?.allowMultiple === true;
    try {
      const result = await uploadFileToServer(file, 'node_doc', {
        bizId: orderId,
        bizId2: nodeCode,
        bizId3: docType,
      });
      if (result && result.id) {
        const { isImage, isPdf } = detectFileType(file.name);
        let previewUrl: string | undefined;
        try {
          previewUrl = await choosePreviewUrl(result.id, isPdf);
        } catch {
          previewUrl = undefined;
        }
        const entry: UploadedFile = {
          name: file.name,
          fileId: result.id,
          previewUrl,
          isImage,
          isPdf,
        };
        setUploadedFiles((prev) => {
          const next = { ...prev };
          next[docType] = allowMultiple ? [...(prev[docType] || []), entry] : [entry];
          return next;
        });
      }
    } catch (err) {
      console.error('节点附件上传失败', err);
      // 降级到本地预览（仅图片可本地预览；PDF 不转 base64，避免大文件拖慢）
      const { isImage, isPdf } = detectFileType(file.name);
      if (!isImage) {
        const entry: UploadedFile = { name: file.name, fileId: undefined, previewUrl: undefined, isImage: false, isPdf: true };
        setUploadedFiles((prev) => {
          const next = { ...prev };
          next[docType] = allowMultiple ? [...(prev[docType] || []), entry] : [entry];
          return next;
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const entry: UploadedFile = { name: file.name, previewUrl: ev.target?.result as string, isImage: true, isPdf: false };
        setUploadedFiles((prev) => {
          const next = { ...prev };
          next[docType] = allowMultiple ? [...(prev[docType] || []), entry] : [entry];
          return next;
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (docType: string, index: number) => {
    const file = uploadedFiles[docType]?.[index];
    if (file?.fileId) {
      try {
        await deleteFile(file.fileId);
      } catch (err) {
        console.error('删除节点附件失败', err);
      }
    }
    setUploadedFiles((prev) => {
      const arr = (prev[docType] || []).filter((_, i) => i !== index);
      const next = { ...prev };
      if (arr.length > 0) next[docType] = arr;
      else delete next[docType];
      return next;
    });
  };

  if (rules.length === 0) return null;

  const allRequiredUploaded = rules
    .filter((r) => r.required)
    .every((r) => (uploadedFiles[r.docType]?.length ?? 0) > 0);

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '18px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(16,24,40,.04),0 6px 18px rgba(16,24,40,.07)',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>节点需上传</Typography>
        {loading ? (
          <CircularProgress size={14} />
        ) : (
          <Typography sx={{ fontSize: '11px', color: allRequiredUploaded ? '#18A06B' : '#F0A020' }}>
            {allRequiredUploaded ? '✓ 已齐全' : '有必填项待上传'}
          </Typography>
        )}
      </Box>
      {weChat && (
        <Typography sx={{ fontSize: '11px', color: '#E65100', mb: 1 }}>
          微信内仅支持拍摄/相册图片上传；如需上传 PDF 文件，请点击右上角 ··· 选择「在浏览器打开」
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rules.map((rule) => {
          const files = uploadedFiles[rule.docType] || [];
          const uploaded = files.length > 0;
          const allowMultiple = rule.allowMultiple === true;
          return (
            <Box key={rule.docType}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '13px' }}>{getLabel('doc_type', rule.docType)}</Typography>
                  {rule.required && <Typography sx={{ fontSize: '11px', color: '#E53935' }}>*</Typography>}
                  {uploaded && (
                    <Typography sx={{ fontSize: '11px', color: '#18A06B', ml: 1 }}>
                      ✓ 已上传{allowMultiple ? ` ${files.length} 份` : ''}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  component="label"
                  variant={uploaded ? 'text' : 'outlined'}
                  sx={{ fontSize: '11px', color: uploaded ? '#F0A020' : undefined }}
                >
                  {allowMultiple ? (uploaded ? '继续添加' : '上传多份') : uploaded ? '重新上传' : '上传'}
                  <input
                    type="file"
                    accept={weChat ? 'image/*' : '.pdf,.jpg,.jpeg,.png'}
                    multiple={allowMultiple}
                    hidden
                    onChange={(e) => {
                      const picked = Array.from(e.target.files ?? []);
                      picked.forEach((file) => handleUpload(rule.docType, file));
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
              {files.map((f, idx) => (
                <Box
                  key={f.fileId || `${f.name}-${idx}`}
                  sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '6px',
                  }}
                >
                  {(() => {
                    if (f.isImage && f.previewUrl) {
                      return (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '4px',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => setPreviewFile({ url: f.previewUrl!, name: f.name, type: f.isPdf ? 'pdf' : 'image' })}
                        >
                          <img src={f.previewUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      );
                    }
                    if (f.isPdf) {
                      return (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '4px',
                            bgcolor: '#FFEBEE',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#C62828',
                            flexShrink: 0,
                            cursor: f.previewUrl ? 'pointer' : 'default',
                          }}
                          onClick={() => f.previewUrl && setPreviewFile({ url: f.previewUrl, name: f.name, type: 'pdf' })}
                        >
                          PDF
                        </Box>
                      );
                    }
                    return (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          bgcolor: '#FFF3E0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          flexShrink: 0,
                        }}
                      >
                        📄
                      </Box>
                    );
                  })()}
                  <Typography sx={{ fontSize: '12px', color: 'primary.main', flex: 1, wordBreak: 'break-all' }}>{f.name}</Typography>
                  <IconButton size="small" title="删除这份文件" onClick={() => handleDelete(rule.docType, idx)} sx={{ color: '#E53935' }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>

      <ImagePreview
        open={!!previewFile}
        url={previewFile?.url}
        name={previewFile?.name}
        type={previewFile?.type}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}
